/** SUNTRONIX ONE — Device Passport (§60).
 *  Full history of a customer-owned asset — one serial, one permanent story.
 *  Shows current facts (top) + full lifecycle timeline (below).  PII of previous
 *  employees is masked to "Previously Deployed · N times" unless the caller is
 *  Suntronix internal (server keeps enforcing the deeper rule).
 */
import LifecycleTimeline from "@/components/app/LifecycleTimeline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ChevronLeft, Loader2, ShieldCheck, XCircle, AlertCircle, Filter, Flame } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DevicePassport() {
  const { assetId } = useParams();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [serialHistory, setSerialHistory] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  const [exceptions, setExceptions] = useState({ exceptions: [], escalations: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [changeFilterField, setChangeFilterField] = useState("");
  const [changeFilterAuthor, setChangeFilterAuthor] = useState("");
  const [changeFilterFrom, setChangeFilterFrom] = useState("");
  const [changeFilterTo, setChangeFilterTo] = useState("");
  const isSuntronix = !user?.master_company_id;

  const loadExceptions = useCallback(async () => {
    if (!isSuntronix) return;
    try {
      const { data } = await api.get(`/exceptions/for-asset/${assetId}`);
      setExceptions(data);
    } catch { /* not fatal */ }
  }, [assetId, isSuntronix]);

  useEffect(() => { loadExceptions(); }, [loadExceptions]);

  useEffect(() => {
    api.get(`/assets/${assetId}`)
      .then(({ data }) => setAsset(data))
      .catch(() => setAsset(null))
      .finally(() => setLoading(false));
    api.get(`/assets/${assetId}/timeline`)
      .then(({ data }) => {
        const events = data.items || data.events || [];
        setSerialHistory(events.filter((e) =>
          e.event_type === "field_serial_changed"
          || e.event_type === "field_serial_number_changed"));
        // All field_*_changed events (including the ones with a reason)
        setChangeHistory(events.filter((e) =>
          typeof e.event_type === "string"
            && e.event_type.startsWith("field_")
            && e.event_type.endsWith("_changed")));
      })
      .catch(() => { setSerialHistory([]); setChangeHistory([]); });
  }, [assetId]);

  if (loading) return <div className="p-8 text-center text-[12px] text-[color:var(--st-text-muted)]"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading passport…</div>;
  if (!asset) return <div className="p-8 text-center text-[13px] text-[color:var(--st-text-muted)]">Asset not found.</div>;

  const cycles = asset.deployment_cycles || [];
  const readiness = computeReadiness(asset);

  return (
    <div className="max-w-4xl space-y-5" data-testid={`device-passport-${assetId}`}>
      <Link to="/inventory" className="inline-flex items-center gap-1 text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
        <ChevronLeft className="h-3.5 w-3.5" /> Inventory
      </Link>

      {/* Open-exception chip strip — field engineer sees any active
          problem before touching the device. */}
      {exceptions.count > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-[12px]"
              data-testid="passport-exception-chip">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-red-800 font-semibold mb-1">
            <Flame className="h-3 w-3" /> Open exceptions · {exceptions.count}
          </div>
          <div className="space-y-1">
            {exceptions.exceptions.map((e) => (
              <div key={`${e.code}-${e.entity_id}`} className="text-slate-800 flex flex-wrap items-center gap-x-2">
                <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border
                  ${e.severity === "high" ? "bg-red-100 text-red-800 border-red-300"
                                             : "bg-amber-100 text-amber-800 border-amber-300"}`}>
                  {e.severity}
                </span>
                <span>{e.title}</span>
                <span className="text-slate-500">· {e.age_hours}h old (SLA {e.sla_hours}h)</span>
              </div>
            ))}
            {exceptions.escalations.map((s) => (
              <div key={s.id} className="text-slate-800 flex flex-wrap items-center gap-x-2">
                <span className="inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border bg-purple-50 text-purple-800 border-purple-300">
                  Escalated
                </span>
                <span>{s.reason}</span>
                <span className="text-slate-500">· by {s.escalated_by_email}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Serial correction strip — surface every past correction so
          operators can trust the identifier they're looking at. */}
      {serialHistory.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px]"
          data-testid="passport-serial-history">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-1">
            <AlertCircle className="h-3 w-3" /> Serial number history
          </div>
          <div className="space-y-1">
            {serialHistory.slice().reverse().map((e) => (
              <div key={e.id || `${e.event_at}-${e.old_value}`}
                className="text-slate-800 flex flex-wrap gap-x-2 gap-y-0.5"
                data-testid={`serial-change-${e.id || e.event_at}`}>
                <span>Corrected from <span className="font-mono text-slate-900">{e.old_value || "—"}</span> to <span className="font-mono text-slate-900">{e.new_value || "—"}</span></span>
                {e.user_email && <span className="text-slate-500">· by {e.user_email}</span>}
                {(e.event_at || e.created_at || e.timestamp) && <span className="text-slate-500">· {shortDate(e.event_at || e.created_at || e.timestamp)}</span>}
                {(e.reason || e.notes) && <span className="text-slate-500">· {e.reason || e.notes}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Every field-change event with its reason — full audit trail
          for the compliance / QA reviewer. */}
      <PassportChangesPanel
        changes={changeHistory}
        field={changeFilterField} setField={setChangeFilterField}
        author={changeFilterAuthor} setAuthor={setChangeFilterAuthor}
        from={changeFilterFrom} setFrom={setChangeFilterFrom}
        to={changeFilterTo} setTo={setChangeFilterTo}
      />


      {/* Lifecycle */}
      <div className="st-panel p-5" data-testid="passport-lifecycle">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Device lifecycle</div>
          <a href={`/lifecycle/device/${asset.id}`} className="text-[11px] text-sky-700 hover:underline" data-testid="passport-lifecycle-full">Full view</a>
        </div>
        <LifecycleTimeline kind="device" id={asset.id} compact />
      </div>

      {/* Passport header */}
      <div className="st-panel p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Device Passport</div>
            <h1 className="font-heading text-[24px] font-medium tracking-[-0.01em] mt-0.5">{asset.model || asset.name || "Device"}</h1>
            <div className="text-[12px] text-[color:var(--st-text-muted)] mt-1 tabular-nums">
              {(asset.serial || asset.serial_number) ? `Serial ${asset.serial || asset.serial_number}` : ""} {asset.asset_tag ? ` · Tag ${asset.asset_tag}` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(asset.serial || asset.serial_number) && (
              <a href={`/devices/${encodeURIComponent(asset.serial || asset.serial_number)}`} data-testid="passport-open-360"
                className="h-8 px-3 rounded-full border border-[color:var(--st-border)] bg-white text-[12px] font-medium inline-flex items-center hover:border-[color:var(--st-border-strong)]">Device 360° →</a>
            )}
            <ReadinessBadge readiness={readiness} />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-[12px]">
          <Fact label="Ownership" value={asset.ownership_type || "customer"} />
          <Fact label="Custody"   value={humanize(asset.custody_type)} />
          <Fact label="Assignment" value={humanize(asset.assignment_type) || "Unassigned"} />
          <Fact label="Availability" value={humanize(asset.availability)} highlight />
          <Fact label="Condition" value={asset.condition_grade || "—"} />
          <Fact label="Warranty" value={asset.warranty_active ? "Active" : (asset.warranty_expiry || "—")} />
          <Fact label="Deployments" value={String(cycles.length)} />
          <Fact label="Location" value={asset.location || asset.warehouse || "—"} />
        </div>
      </div>

      {/* Readiness checklist */}
      <div className="st-panel p-5" data-testid="passport-readiness">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Readiness</div>
        <div className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-1.5">
          {readiness.checks.map((c) => (
            <div key={c.label} className="flex items-center gap-2 text-[13px]">
              {c.pass
                ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                : <XCircle className="h-3.5 w-3.5 text-[color:var(--st-warn)]" />}
              <span className="text-[color:var(--st-text)]">{c.label}</span>
              {!c.pass && c.reason && <span className="ml-auto text-[11px] text-[color:var(--st-text-muted)]">{c.reason}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Deployment cycles */}
      <div className="st-panel p-5" data-testid="passport-cycles">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Deployment history</div>
        {cycles.length === 0 ? (
          <div className="text-[13px] text-[color:var(--st-text-muted)] mt-2">Never deployed.  Currently held at Suntronix.</div>
        ) : (
          <ol className="mt-4 relative pl-6 space-y-4">
            <span className="absolute left-[7px] top-1 bottom-1 w-px bg-[color:var(--st-border-strong)]" />
            {cycles.map((c) => (
              <li key={c.cycle_no} className="relative">
                <span className="absolute -left-6 top-1 h-[15px] w-[15px] rounded-full border border-[color:var(--st-border-strong)] bg-[color:var(--st-bg)]" />
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-[13px] font-semibold uppercase tracking-[0.14em]">Deployment #{c.cycle_no}</div>
                  <div className="text-[11px] text-[color:var(--st-text-muted)]">{shortDate(c.assigned_at)} — {c.recovered_at ? shortDate(c.recovered_at) : "current"}</div>
                </div>
                <div className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5">
                  {isSuntronix
                    ? `Employee ${c.employee_id || "—"}`
                    : "Previously deployed"}
                  {c.request_id ? ` · Request ${c.request_id}` : ""}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function PassportChangesPanel({ changes, field, setField, author, setAuthor,
                                       from, setFrom, to, setTo }) {
  const fieldOptions = useMemo(() => {
    const set = new Set();
    for (const e of (changes || [])) {
      const f = (e.event_type || "").replace(/^field_/, "").replace(/_changed$/, "");
      if (f) set.add(f);
    }
    return Array.from(set).sort();
  }, [changes]);

  const filtered = useMemo(() => {
    if (!changes) return [];
    const fq = (field || "").toLowerCase();
    const aq = (author || "").toLowerCase();
    const fromT = from ? new Date(from).getTime() : null;
    // include the whole "to" day
    const toT = to ? new Date(to + "T23:59:59Z").getTime() : null;
    return changes.filter((e) => {
      if (fq) {
        const f = (e.event_type || "").toLowerCase();
        if (!f.includes(fq)) return false;
      }
      if (aq) {
        const who = (e.user_email || e.actor || e.user || "").toLowerCase();
        if (!who.includes(aq)) return false;
      }
      const t = new Date(e.event_at || e.created_at || e.timestamp || 0).getTime();
      if (fromT && (Number.isNaN(t) || t < fromT)) return false;
      if (toT && (Number.isNaN(t) || t > toT)) return false;
      return true;
    });
  }, [changes, field, author, from, to]);

  if (!changes || changes.length === 0) return null;
  const clearAll = () => { setField(""); setAuthor(""); setFrom(""); setTo(""); };
  const hasFilter = field || author || from || to;

  return (
    <div className="st-panel p-5" data-testid="passport-changes-tab">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">
          Changes
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">
          {filtered.length} / {changes.length} event{changes.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Filter strip — field / author / date range */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-[minmax(0,160px)_minmax(0,1fr)_minmax(0,140px)_minmax(0,140px)_auto] gap-2 items-end"
             data-testid="passport-changes-filters">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5 flex items-center gap-1">
            <Filter className="h-3 w-3" /> Field
          </div>
          <select value={field} onChange={(e) => setField(e.target.value)}
             className="h-8 w-full border rounded px-1 text-[12px]"
             data-testid="passport-changes-filter-field">
            <option value="">All</option>
            {fieldOptions.map((f) => (
              <option key={f} value={f}>{f.replace(/_/g, " ")}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">Author</div>
          <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)}
             placeholder="email / user"
             className="h-8 w-full border rounded px-2 text-[12px]"
             data-testid="passport-changes-filter-author" />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">From</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
             className="h-8 w-full border rounded px-1 text-[12px]"
             data-testid="passport-changes-filter-from" />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">To</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
             className="h-8 w-full border rounded px-1 text-[12px]"
             data-testid="passport-changes-filter-to" />
        </div>
        {hasFilter && (
          <button type="button" onClick={clearAll}
              className="h-8 rounded border border-slate-200 hover:bg-slate-50 px-2 text-[11px] uppercase tracking-wider text-slate-600"
              data-testid="passport-changes-filter-clear">
            Clear
          </button>
        )}
      </div>

      <div className="mt-3 divide-y divide-slate-100 max-h-80 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="text-[12px] text-slate-400 italic py-4 text-center"
                data-testid="passport-changes-empty">
            No events match the current filter.
          </div>
        )}
        {filtered.slice().reverse().map((e, i) => {
          const f = (e.event_type || "").replace(/^field_/, "").replace(/_changed$/, "").replace(/_/g, " ");
          return (
            <div key={e.id || i} className="py-2 text-[13px] flex flex-wrap gap-x-2 gap-y-0.5"
              data-testid={`change-event-${e.id || i}`}>
              <span className="font-medium capitalize text-slate-800 w-32 shrink-0">{f || "field"}</span>
              <span className="text-slate-500">
                <span className="font-mono text-slate-700">{String(e.old_value ?? "—")}</span>
                <span className="mx-1">→</span>
                <span className="font-mono text-slate-900">{String(e.new_value ?? "—")}</span>
              </span>
              <span className="ml-auto text-[11px] text-slate-500">
                {e.user_email && <>by {e.user_email} · </>}
                {shortDate(e.event_at || e.created_at || e.timestamp)}
              </span>
              {(e.reason || e.notes) && (
                <div className="basis-full text-[11px] text-slate-600 pl-32">
                  Reason: {e.reason || e.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Fact({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)]">{label}</div>
      <div className={"text-[13px] mt-0.5 " + (highlight ? "font-semibold text-[color:var(--st-accent)]" : "text-[color:var(--st-text)]")}>{value || "—"}</div>
    </div>
  );
}

function ReadinessBadge({ readiness }) {
  if (readiness.ready) {
    return (
      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-[0.14em]" data-testid="passport-ready">
        <ShieldCheck className="h-3.5 w-3.5" /> Ready for redeployment
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-amber-100 text-amber-900 text-[11px] font-semibold uppercase tracking-[0.14em]" data-testid="passport-not-ready">
      <AlertCircle className="h-3.5 w-3.5" /> Not ready
    </span>
  );
}

function computeReadiness(a) {
  const checks = [
    { label: "Inspection passed",    pass: a.inspection_status === "passed" || a.availability === "ready_for_redeployment", reason: "Awaiting inspection" },
    { label: "Cleaning completed",   pass: a.cleaning_completed === true    || a.availability === "ready_for_redeployment", reason: "Cleaning pending" },
    { label: "Data erasure verified", pass: a.erasure_status === "verified"  || a.availability === "ready_for_redeployment", reason: "Erasure required" },
    { label: "Repair complete",       pass: a.availability !== "repair_required",                                            reason: "Repair required" },
    { label: "No security hold",      pass: a.availability !== "quarantined",                                                reason: "Quarantined" },
    { label: "Accessories checked",   pass: (a.accessories_status || "ok") !== "missing",                                    reason: "Missing accessories" },
    { label: "Customer policy",       pass: (a.policy_hold || "") !== "hold",                                                reason: "Awaiting customer approval" },
  ];
  return { checks, ready: checks.every((c) => c.pass) };
}

function humanize(v) { return v ? String(v).replace(/_/g, " ") : ""; }
function shortDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } }
